import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { debounceTime, distinctUntilChanged } from 'rxjs'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatOption } from '@angular/material/core'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { PurchaseServiceOrderService } from '../../../core/services/purchase-reservation/purchase-service-order/purchase-service-order.service'
import { SupplierService } from '../../../core/services/person/supplier/supplier.service'
import { AuthService } from '../../../core/services/auth/auth.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { CurrencyInputDirective } from '../../../shared/directives/currencyInput'
import { convertCurrency } from '../../../shared/utils/utils'
import { ErrorRequestModel } from '../../../core/models/response.model'
import { EditPurchaseServiceOrderModel } from '../../../core/models/purchase-reservation.model'

const GESTOR_RESERVA_COMPRAS_PERFIL_ID = 13

@Component({
  selector: 'purchase-reservation-edit-modal',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatButton,
    MatProgressBar,
    MatProgressSpinner,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOption,
    MatCheckboxModule,
    MatIconModule,
    CurrencyInputDirective,
  ],
  templateUrl: './purchase-reservation-edit-modal.component.html',
  styleUrl: './purchase-reservation-edit-modal.component.scss',
  standalone: true,
})
export class PurchaseReservationEditModalComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private comprasOrdemServicoService: PurchaseServiceOrderService,
    private supplierService: SupplierService,
    private authService: AuthService,
    private snackbar: SnackbarCustomService,
    private dialogRef: MatDialogRef<PurchaseReservationEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.item = data.item
  }

  @Output() response = new EventEmitter()

  item: any = {}
  cos: any = null
  loading = false
  saving = false
  suppliers: any[] = []
  form: FormGroup = new FormGroup({})
  prazoEdit: number | null = null
  savingPrazo = false
  supplierFilterForm: FormGroup = new FormGroup({})

  get isGestor(): boolean {
    return (
      this.authService.getInfosObject()?.idPerfil ==
      GESTOR_RESERVA_COMPRAS_PERFIL_ID
    )
  }

  get prazoPagamentoSelecionado(): number | null {
    const fornecedorId = this.form.get('fornecedorId')?.value
    const supplier = this.suppliers.find((s: any) => s.id == fornecedorId)
    return supplier?.prazoPagamento ?? this.cos?.prazoPagamento ?? null
  }

  get paxDisplay(): number | string {
    return this.item?.pax ?? this.item?.quantidadePassageiros ?? '-'
  }

  get enderecoCompletoDisplay(): string {
    if (this.item?.enderecoCompleto) {
      return this.item.enderecoCompleto
    }
    if (this.item?.descricaoOrigem || this.item?.descricaoDestino) {
      return `${this.item?.descricaoOrigem ?? '-'} → ${this.item?.descricaoDestino ?? '-'}`
    }
    return '-'
  }

  get vendaComTaxa(): number | null {
    return this.cos?.valorVendaComTaxa ?? this.item?.valorVendaComTaxa ?? null
  }

  get vendaComTaxaDisplay(): string {
    return this.vendaComTaxa != null
      ? `${convertCurrency(this.vendaComTaxa)}`
      : '-'
  }

  get targetDisplay(): string {
    const venda = this.vendaComTaxa
    const multiplicador = this.form.get('multiplicadorVenda')?.value
    if (venda != null && multiplicador != null && multiplicador !== '') {
      return `${convertCurrency(venda * multiplicador)}`
    }
    return this.cos?.target != null
      ? `${convertCurrency(this.cos.target)}`
      : '-'
  }

  get savingsDisplay(): string {
    const tarifario = this.cos?.valorTarifario
    const compraRaw = this.form.get('valorCompra')?.value
    if (tarifario != null && compraRaw) {
      const compra = convertCurrency(compraRaw)
      if (typeof compra === 'number' && !isNaN(compra)) {
        return this.formatSavings(tarifario - compra, tarifario)
      }
    }
    if (this.cos?.savings != null) {
      return this.formatSavings(this.cos.savings, tarifario)
    }
    return '-'
  }

  private formatSavings(savings: number, tarifario?: number | null): string {
    const valor = `${convertCurrency(savings)}`
    if (!tarifario) {
      return valor
    }
    const percentual = (savings / tarifario) * 100
    return `${valor} (${percentual.toFixed(1)}%)`
  }

  ngOnInit(): void {
    this.createForm()
    this.createSupplierFilterForm()
    this.listSuppliers()
    this.loadOrCreateCos()
  }

  createSupplierFilterForm(): void {
    this.supplierFilterForm = this.formBuilder.group({
      nome: [''],
      cnpj: [''],
      cidade: [''],
    })

    this.supplierFilterForm.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
      .subscribe(() => this.listSuppliers())
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      fornecedorId: ['', Validators.required],
      valorCompra: ['', Validators.required],
      freelancer: [false],
      valorMotorista: [{ value: '', disabled: true }],
      multiplicadorVenda: [{ value: 0.55, disabled: !this.isGestor }],
      observacaoOs: [''],
      obsFornecedorOperador: [''],
    })

    this.form.get('freelancer')?.valueChanges.subscribe((freelancer: boolean) => {
      const control = this.form.get('valorMotorista')
      if (freelancer) {
        control?.enable()
      } else {
        control?.setValue('')
        control?.disable()
      }
    })

    this.form.get('fornecedorId')?.valueChanges.subscribe(() => {
      this.prazoEdit = this.prazoPagamentoSelecionado
    })
  }

  listSuppliers() {
    const { nome, cnpj, cidade } = this.supplierFilterForm.value
    this.supplierService.listSupplier('S', nome, cnpj, cidade).subscribe({
      next: (response: any) => {
        this.suppliers = response.data ?? []
      },
      error: () => {
        this.suppliers = []
      },
    })
  }

  loadOrCreateCos() {
    this.loading = true
    this.comprasOrdemServicoService.getByOsId(this.item.codigoOs).subscribe({
      next: (response: any) => {
        if (response.statusCode == 206) {
          this.createCos()
        } else {
          this.fillForm(response.data)
          this.loading = false
        }
      },
      error: (err: ErrorRequestModel) => {
        this.loading = false
        this.snackbar.errorRequest(err)
        this.close()
      },
    })
  }

  createCos() {
    const model = {
      ordemServicoId: this.item.codigoOs,
      tipoVeiculoId: this.item.tipoVeiculoId ?? null,
      tipoMotoristaId: this.item.tipoMotoristaId ?? null,
      quantidadeVeiculo: this.item.quantidadeVeiculo ?? 1,
      quantidadeMotorista: this.item.quantidadeMotorista ?? 1,
    }
    this.comprasOrdemServicoService.create(model).subscribe({
      next: (response: any) => {
        this.fillForm(response.data)
        this.loading = false
      },
      error: (err: ErrorRequestModel) => {
        this.loading = false
        this.snackbar.errorRequest(err)
        this.close()
      },
    })
  }

  fillForm(cos: any) {
    this.cos = cos
    this.form.get('fornecedorId')?.setValue(cos.fornecedorId ?? '')
    this.form
      .get('valorCompra')
      ?.setValue(cos.valorCompra ? convertCurrency(cos.valorCompra) : '')
    this.form.get('freelancer')?.setValue(!!cos.freelancer)
    const valorMotoristaControl = this.form.get('valorMotorista')
    valorMotoristaControl?.setValue(
      cos.valorMotorista ? convertCurrency(cos.valorMotorista) : '',
    )
    if (cos.freelancer) {
      valorMotoristaControl?.enable()
    } else {
      valorMotoristaControl?.disable()
    }
    this.form
      .get('multiplicadorVenda')
      ?.setValue(cos.multiplicadorVenda ?? 0.55)
    this.form.get('observacaoOs')?.setValue(cos.observacaoOs ?? '')
    this.form
      .get('obsFornecedorOperador')
      ?.setValue(cos.obsFornecedorOperador ?? '')
    this.prazoEdit = this.prazoPagamentoSelecionado
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }
    this.saving = true
    const freelancer = this.form.get('freelancer')!.value
    const model: EditPurchaseServiceOrderModel = {
      id: this.cos.id,
      fornecedorId: this.form.get('fornecedorId')!.value,
      valorCompra: convertCurrency(
        this.form.get('valorCompra')!.value,
      ) as number,
      freelancer,
      valorMotorista: freelancer
        ? (convertCurrency(this.form.get('valorMotorista')!.value) as number)
        : 0,
      enviarEmail: false,
      observacaoOs: this.form.get('observacaoOs')!.value || null,
      obsFornecedorOperador:
        this.form.get('obsFornecedorOperador')!.value || null,
    }
    if (this.isGestor) {
      model.multiplicadorVenda = this.form.get('multiplicadorVenda')!.value
    }

    this.comprasOrdemServicoService.editComprasOs(model).subscribe({
      next: (response: any) => {
        this.saving = false
        this.snackbar.success(response.message)
        this.response.emit(true)
        this.close()
      },
      error: (err: ErrorRequestModel) => {
        this.saving = false
        this.snackbar.errorRequest(err)
      },
    })
  }

  salvarPrazo() {
    const fornecedorId = this.form.get('fornecedorId')?.value
    if (!fornecedorId) {
      this.snackbar.warning('Selecione um fornecedor antes de salvar o prazo.')
      return
    }
    this.savingPrazo = true
    this.supplierService
      .editPrazoPagamento(fornecedorId, { prazoPagamento: this.prazoEdit })
      .subscribe({
        next: (response: any) => {
          this.savingPrazo = false
          this.snackbar.success(response.message)
          const supplier = this.suppliers.find((s: any) => s.id == fornecedorId)
          if (supplier) {
            supplier.prazoPagamento = this.prazoEdit
          }
          if (this.cos) {
            this.cos.prazoPagamento = this.prazoEdit
          }
        },
        error: (err: ErrorRequestModel) => {
          this.savingPrazo = false
          this.snackbar.errorRequest(err)
        },
      })
  }

  close() {
    this.dialogRef.close()
  }
}
